import { useState, useCallback, useMemo, useRef, useEffect, startTransition } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  type DragStartEvent,
  type DragMoveEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { WidgetWrapper } from "./WidgetWrapper";
import { WIDGET_REGISTRY } from "./widgets";
import { WidgetPicker } from "./WidgetPicker";
import { PresetSelector } from "./PresetSelector";
import { CustomChartBuilder } from "./CustomChartBuilder";
import { WidgetConfigDialog } from "./WidgetConfigDialog";
import type { DashboardShellProps, DashboardLayout, WidgetConfig, LayoutItem } from "./types";

// ─── Constants ────────────────────────────────────────────────────────────────
const COLS = 12;
const ROW_HEIGHT = 30; // px per grid row unit
const GAP = 12;        // px gap between cells

// ─── Types ────────────────────────────────────────────────────────────────────
type WidgetSizeLimits = { minW?: number; minH?: number; maxW?: number; maxH?: number };
type RepackOptions = {
  autoGrow?: boolean;
  pinnedItemId?: string | null;
  preferredPlacements?: Map<string, { x: number; y: number }>;
};
type ResizeState = {
  id: string;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
  base: LayoutItem[]; // snapshot at resize-start — never changes during resize
  item: LayoutItem;
};

// ─── Utilities ────────────────────────────────────────────────────────────────
function collides(a: LayoutItem, b: LayoutItem): boolean {
  return (
    a.x < b.x + b.w && a.x + a.w > b.x &&
    a.y < b.y + b.h && a.y + a.h > b.y
  );
}

function containsGridPoint(item: LayoutItem, gx: number, gy: number): boolean {
  return gx >= item.x && gx < item.x + item.w && gy >= item.y && gy < item.y + item.h;
}

function applyHardSwapToLayout(layout: LayoutItem[], activeId: string, targetId: string): LayoutItem[] {
  const active = layout.find((l) => l.i === activeId);
  const target = layout.find((l) => l.i === targetId);
  if (!active || !target) return layout;
  return layout.map((item) => {
    if (item.i === activeId) return { ...item, x: target.x, y: target.y, w: target.w, h: target.h };
    if (item.i === targetId) return { ...item, x: active.x, y: active.y, w: active.w, h: active.h };
    return item;
  });
}

function clampLayoutItem(item: LayoutItem, sizeLimitsById?: Map<string, WidgetSizeLimits>): LayoutItem {
  const limits = sizeLimitsById?.get(item.i);
  const minW = Math.max(1, Math.min(COLS, limits?.minW ?? 1));
  const maxW = Math.max(minW, Math.min(COLS, limits?.maxW ?? COLS));
  const minH = Math.max(1, limits?.minH ?? 1);
  const maxH = Math.max(minH, limits?.maxH ?? Number.MAX_SAFE_INTEGER);
  const w = Math.max(minW, Math.min(maxW, Math.max(1, item.w)));
  const h = Math.max(minH, Math.min(maxH, Math.max(1, item.h)));

  return {
    ...item,
    x: Math.max(0, Math.min(item.x, COLS - w)),
    y: Math.max(0, item.y),
    w,
    h,
    minW,
    minH,
    maxW,
    maxH: Number.isFinite(maxH) ? maxH : undefined,
  };
}

function sanitizeLayoutItems(
  layout: LayoutItem[],
  validIds?: Set<string>,
  sizeLimitsById?: Map<string, WidgetSizeLimits>,
): LayoutItem[] {
  const deduped = new Map<string, LayoutItem>();
  for (const raw of layout) {
    if (!raw || typeof raw.i !== "string" || raw.i.length === 0) continue;
    if (validIds && !validIds.has(raw.i)) continue;
    if (![raw.x, raw.y, raw.w, raw.h].every((v) => Number.isFinite(v))) continue;
    deduped.set(raw.i, clampLayoutItem(raw, sizeLimitsById));
  }
  return [...deduped.values()];
}

function stabilizeLockedTables(
  gridLayout: LayoutItem[],
  widgets: WidgetConfig[],
  anchorGridLayout?: LayoutItem[],
): LayoutItem[] {
  const lockedTableIds = widgets
    .filter((widget) => widget.type === "table")
    .map((widget) => widget.id);

  if (lockedTableIds.length !== 1) {
    return gridLayout;
  }

  const lockedId = lockedTableIds[0];
  const lockedItem = gridLayout.find((item) => item.i === lockedId);
  if (!lockedItem) {
    return gridLayout;
  }

  const unlockedBottom = gridLayout
    .filter((item) => item.i !== lockedId)
    .reduce((max, item) => Math.max(max, item.y + item.h), 0);
  const anchorLockedItem = anchorGridLayout?.find((item) => item.i === lockedId);

  return gridLayout.map((item) =>
    item.i === lockedId
      ? {
          ...item,
          x: anchorLockedItem?.x ?? item.x,
          y: unlockedBottom,
          w: anchorLockedItem?.w ?? item.w,
          h: anchorLockedItem?.h ?? item.h,
        }
      : item,
  );
}

function normalizeDashboardLayout(layout: DashboardLayout, anchorLayout?: DashboardLayout): DashboardLayout {
  const widgets = layout.widgets.map((widget) => ({ ...widget }));
  const widgetIdSet = new Set(widgets.map((widget) => widget.id).filter(Boolean));
  const sizeLimitsById = buildSizeLimitsById(widgets);
  const normalizedGridLayout = repackLayout(
    sanitizeLayoutItems(layout.gridLayout, widgetIdSet, sizeLimitsById),
    sizeLimitsById,
    { autoGrow: false },
  );

  return {
    widgets,
    gridLayout: stabilizeLockedTables(normalizedGridLayout, widgets, anchorLayout?.gridLayout),
  };
}

function applyPreferredPlacement(
  layout: LayoutItem[],
  itemId: string,
  placement: { x: number; y: number },
): LayoutItem[] {
  return layout.map((item) =>
    item.i === itemId
      ? {
          ...item,
          x: Math.max(0, Math.min(placement.x, COLS - item.w)),
          y: Math.max(0, placement.y),
        }
      : item,
  );
}

function getLayoutBottom(layout: LayoutItem[]): number {
  return layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
}

function getSingleLockedTableId(widgets: WidgetConfig[]): string | null {
  const tableIds = widgets.filter((widget) => widget.type === "table").map((widget) => widget.id);
  return tableIds.length === 1 ? tableIds[0] ?? null : null;
}

function getInsertStartY(layout: LayoutItem[], widgets: WidgetConfig[]): number {
  const lockedTableId = getSingleLockedTableId(widgets);
  if (!lockedTableId) {
    return getLayoutBottom(layout);
  }

  const unlockedItems = layout.filter((item) => item.i !== lockedTableId);
  return getLayoutBottom(unlockedItems);
}

function repackLayout(
  layout: LayoutItem[],
  sizeLimitsById?: Map<string, WidgetSizeLimits>,
  options?: RepackOptions,
): LayoutItem[] {
  const autoGrow = options?.autoGrow ?? true;
  const pinnedItemId = options?.pinnedItemId ?? null;
  const preferredPlacements = options?.preferredPlacements;
  const preferredIds = new Set(preferredPlacements?.keys() ?? []);
  const pinnedItem = pinnedItemId ? layout.find((item) => item.i === pinnedItemId) : undefined;

  const placementOrder = [...layout]
    .filter((item) => item.i !== pinnedItemId)
    .sort((a, b) => {
      const aP = preferredIds.has(a.i) ? 0 : 1;
      const bP = preferredIds.has(b.i) ? 0 : 1;
      return aP - bP || a.y - b.y || a.x - b.x;
    });

  const placed: LayoutItem[] = [];
  const placedById = new Map<string, LayoutItem>();

  if (pinnedItem) {
    placed.push({ ...pinnedItem });
    placedById.set(pinnedItem.i, placed[0]!);
  }

  for (const item of placementOrder) {
    const preferred = preferredPlacements?.get(item.i);
    if (preferred) {
      const candidate: LayoutItem = {
        ...item,
        x: Math.max(0, Math.min(preferred.x, COLS - item.w)),
        y: Math.max(0, preferred.y),
      };
      if (candidate.x + candidate.w <= COLS && !placed.some((p) => collides(candidate, p))) {
        placed.push(candidate);
        placedById.set(item.i, candidate);
        continue;
      }
    }

    const maxX = COLS - item.w;
    if (maxX < 0) {
      const oversized = { ...item, x: 0, w: COLS };
      placed.push(oversized);
      placedById.set(item.i, oversized);
      continue;
    }

    let next: LayoutItem | null = null;
    for (let y = 0; next === null; y++) {
      for (let x = 0; x <= maxX; x++) {
        const candidate = { ...item, x, y };
        if (!placed.some((p) => collides(candidate, p))) { next = candidate; break; }
      }
    }
    if (!next) continue;
    placed.push(next);
    placedById.set(item.i, next);
  }

  if (!autoGrow) {
    return layout.map((item) => placedById.get(item.i) ?? item);
  }

  const initialBottom = placed.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  const grown = placed.map((item) => ({ ...item }));
  const orderedIds = [...grown]
    .filter((item) => item.i !== pinnedItemId)
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map((item) => item.i);

  let changed = true;
  while (changed) {
    changed = false;
    for (const id of orderedIds) {
      const idx = grown.findIndex((item) => item.i === id);
      if (idx === -1) continue;
      const item = grown[idx];
      if (!item) continue;
      const limits = sizeLimitsById?.get(item.i);
      const maxW = Math.max(item.w, Math.min(COLS, limits?.maxW ?? COLS));
      const maxH = Math.max(item.h, limits?.maxH ?? item.h);

      while (item.w < maxW && item.x + item.w < COLS) {
        const c = { ...item, w: item.w + 1 };
        if (grown.some((o, i) => i !== idx && collides(c, o))) break;
        item.w++; changed = true;
      }
      while (item.h < maxH && item.y + item.h < initialBottom) {
        const c = { ...item, h: item.h + 1 };
        if (grown.some((o, i) => i !== idx && collides(c, o))) break;
        item.h++; changed = true;
      }
    }
  }

  const grownById = new Map(grown.map((item) => [item.i, item]));
  return layout.map((item) => grownById.get(item.i) ?? placedById.get(item.i) ?? item);
}

function buildSizeLimitsById(widgets: WidgetConfig[]): Map<string, WidgetSizeLimits> {
  const map = new Map<string, WidgetSizeLimits>();
  for (const widget of widgets) {
    const entry = WIDGET_REGISTRY[widget.type];
    map.set(widget.id, {
      minW: entry?.minSize?.w, minH: entry?.minSize?.h,
      maxW: entry?.maxSize?.w, maxH: entry?.maxSize?.h,
    });
  }
  return map;
}

function getLayoutSignature(layout: LayoutItem[]): string {
  return sanitizeLayoutItems(layout)
    .map((item) => `${item.i}:${item.x},${item.y},${item.w},${item.h}`)
    .sort().join("|");
}

function getCellWidth(containerWidth: number): number {
  return (containerWidth - (COLS - 1) * GAP) / COLS;
}

function pixelToGrid(px: number, py: number, rect: DOMRect): { gx: number; gy: number } {
  const cellW = getCellWidth(rect.width);
  const cellH = ROW_HEIGHT + GAP;
  const gx = Math.floor((px - rect.left) / (cellW + GAP));
  const gy = Math.floor((py - rect.top) / cellH);
  return { gx: Math.max(0, Math.min(COLS - 1, gx)), gy: Math.max(0, gy) };
}

// ─── Draggable cell — plain div in edit mode, no Framer Motion layout ─────────
interface DraggableCellProps {
  widget: WidgetConfig;
  pos: LayoutItem;
  isEditMode: boolean;
  isLocked: boolean;
  isActive: boolean;       // is this the item being dragged right now?
  containerWidth: number;
  layoutKey: string;
  onRemove: (id: string) => void;
  onConfigure: (id: string) => void;
  onResizeStart: (id: string, e: React.PointerEvent) => void;
  children: React.ReactNode;
}

function DraggableCell({
  widget, pos, isEditMode, isLocked, isActive,
  containerWidth, layoutKey, onRemove, onConfigure, onResizeStart, children,
}: DraggableCellProps) {
  const { listeners, setNodeRef } = useDraggable({
    id: widget.id,
    disabled: !isEditMode || isLocked,
  });

  const cellW = getCellWidth(containerWidth);
  const cellH = ROW_HEIGHT + GAP;

  const style: React.CSSProperties = {
    gridColumn: `${pos.x + 1} / span ${pos.w}`,
    gridRow:    `${pos.y + 1} / span ${pos.h}`,
    // Explicit pixel sizes for the overlay to match exactly
    width:  pos.w * cellW + (pos.w - 1) * GAP,
    height: pos.h * cellH - GAP,
    position: "relative",
    borderRadius: 20,
  };

  return (
    <motion.div
      ref={setNodeRef}
      data-widget-id={widget.id}
      layout={isEditMode}
      layoutDependency={layoutKey}
      transition={{
        layout: isActive
          ? { type: "spring", stiffness: 420, damping: 32 }
          : { type: "spring", stiffness: 320, damping: 34, mass: 0.8 },
      }}
      style={style}
    >
      <WidgetWrapper
        widget={widget}
        isEditMode={isEditMode}
        showEditControls={!isLocked}
        isDragging={isActive}
        dragListeners={isEditMode ? (listeners as Record<string, unknown>) : undefined}
        onRemove={onRemove}
        onConfigure={onConfigure}
      >
        {children}
      </WidgetWrapper>

      {isEditMode && !isLocked && (
        <div
          onPointerDown={(e) => onResizeStart(widget.id, e)}
          style={{ position: "absolute", bottom: 0, right: 0, width: 20, height: 20,
            cursor: "se-resize", zIndex: 30, display: "flex",
            alignItems: "flex-end", justifyContent: "flex-end",
            paddingBottom: 3, paddingRight: 3 }}
          title="Resize"
        >
          <svg width="10" height="10" viewBox="0 0 10 10"
            style={{ color: "var(--muted-foreground)", opacity: 0.5 }}>
            <path d="M9 1 L1 9 M9 5 L5 9"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
      )}
    </motion.div>
  );
}

// ─── Floating overlay card (only thing using Framer Motion during drag) ────────
interface OverlayCardProps {
  widget: WidgetConfig;
  pos: LayoutItem;
  containerWidth: number;
  children: React.ReactNode;
}

function OverlayCard({ widget, pos, containerWidth, children }: OverlayCardProps) {
  const cellW = getCellWidth(containerWidth);
  const cellH = ROW_HEIGHT + GAP;

  return (
    <motion.div
      initial={{ scale: 1, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
      animate={{ scale: 1.04, boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      style={{
        width:  pos.w * cellW + (pos.w - 1) * GAP,
        height: pos.h * cellH - GAP,
        borderRadius: 12,
        overflow: "hidden",
        pointerEvents: "none",
        opacity: 0.92,
      }}
    >
      <WidgetWrapper
        widget={widget}
        isEditMode={true}
        onRemove={() => {/* noop */}}
        onConfigure={() => {/* noop */}}
      >
        {children}
      </WidgetWrapper>
    </motion.div>
  );
}

interface EditorPreviewCardProps {
  widget: WidgetConfig;
  pos: LayoutItem;
  isActive: boolean;
  isResizing: boolean;
}

function EditorPreviewCard({ widget, pos, isActive, isResizing }: EditorPreviewCardProps) {
  const entry = WIDGET_REGISTRY[widget.type];
  const categoryLabel = entry?.category ?? "custom";
  const typeLabel = entry?.label ?? "Widget";
  const accentClass = isResizing
    ? "from-amber-500/20 via-amber-500/8 to-transparent border-amber-500/25"
    : isActive
      ? "from-[var(--brand-primary)]/22 via-[var(--brand-primary)]/8 to-transparent border-[var(--brand-primary)]/30"
      : "from-slate-500/14 via-slate-500/6 to-transparent border-border/70";
  const statusLabel = isResizing ? "Resizing" : isActive ? "Moving" : "Editable";

  return (
    <div
      className={`relative h-full w-full overflow-hidden rounded-[1.25rem] border bg-gradient-to-br ${accentClass}`}
    >
      <div className="absolute inset-0 opacity-60 pointer-events-none" aria-hidden="true">
        <div
          className="h-full w-full"
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 24px)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <div className="relative flex h-full flex-col justify-between p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/80">
              {categoryLabel}
            </p>
            <h3 className="mt-2 truncate text-sm font-semibold text-foreground">
              {widget.title}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {typeLabel}
            </p>
          </div>

          <div className="rounded-full border border-white/10 bg-background/75 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground backdrop-blur-sm">
            {statusLabel}
          </div>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {pos.w} x {pos.h} cells
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-white/25" />
            <span className="h-2 w-2 rounded-full bg-white/18" />
            <span className="h-2 w-2 rounded-full bg-white/12" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DashboardShell ───────────────────────────────────────────────────────────
export function DashboardShell({
  page,
  defaultLayout,
  allowEditing = true,
  filterBar,
  header,
  children,
  childrenPlacement = "beforeGrid",
}: DashboardShellProps) {
  const normalizedDefaultLayout = useMemo(
    () => normalizeDashboardLayout(defaultLayout, defaultLayout),
    [defaultLayout],
  );
  const canEdit = allowEditing;
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentLayout, setCurrentLayout] = useState<DashboardLayout>(normalizedDefaultLayout);
  const [savedLayout, setSavedLayout]     = useState<DashboardLayout>(normalizedDefaultLayout);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [configDialogState, setConfigDialogState] = useState<{
    widgetType: string;
    widget?: WidgetConfig | null;
    layoutItem?: LayoutItem | null;
  } | null>(null);
  const [customChartState, setCustomChartState] = useState<{
    widget?: WidgetConfig | null;
    layoutItem?: LayoutItem | null;
  } | null>(null);
  const [activePresetId, setActivePresetId]     = useState<string | null>(null);
  const [activeId, setActiveId]   = useState<string | null>(null); // drag
  const [containerWidth, setContainerWidth] = useState(1200);

  // Resize preview — separate from currentLayout to avoid setState loops
  const [resizingId, setResizingId] = useState<string | null>(null);
  const [resizeDims, setResizeDims] = useState<{ w: number; h: number } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  // The "true" committed layout — only updated after drag/resize ends or add/remove/preset
  const committedLayoutRef = useRef<LayoutItem[]>(normalizedDefaultLayout.gridLayout.map((l) => ({ ...l })));

  // Snapshot taken at drag-start — base for all swap calculations
  const dragStartLayoutRef = useRef<LayoutItem[]>([]);
  const swapTargetRef = useRef<string | null>(null);
  const dragPlacementRef = useRef<{ x: number; y: number } | null>(null);
  const pendingScrollWidgetIdRef = useRef<string | null>(null);

  // Resize tracking
  const resizeStateRef = useRef<ResizeState | null>(null);
  const finalResizeDimsRef = useRef<{ w: number; h: number } | null>(null);

  // Track container width for cell-size math
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    obs.observe(el);
    setContainerWidth(el.getBoundingClientRect().width);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (canEdit || !isEditMode) return;
    setIsEditMode(false);
    setShowWidgetPicker(false);
    setActiveId(null);
    setResizingId(null);
    setResizeDims(null);
    setConfigDialogState(null);
    setCustomChartState(null);
    swapTargetRef.current = null;
    dragPlacementRef.current = null;
    resizeStateRef.current = null;
    finalResizeDimsRef.current = null;
  }, [canEdit, isEditMode]);

  useEffect(() => {
    const widgetId = pendingScrollWidgetIdRef.current;
    if (!widgetId) return;

    const frame = window.requestAnimationFrame(() => {
      const element = containerRef.current?.querySelector<HTMLElement>(`[data-widget-id="${widgetId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }
      pendingScrollWidgetIdRef.current = null;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [currentLayout.gridLayout, currentLayout.widgets]);

  const widgetIdSet = useMemo(
    () => new Set(currentLayout.widgets.map((w) => w.id).filter(Boolean)),
    [currentLayout.widgets],
  );
  const sizeLimitsById = useMemo(
    () => buildSizeLimitsById(currentLayout.widgets),
    [currentLayout.widgets],
  );
  const isEditorInteracting = isEditMode && (activeId !== null || resizingId !== null);

  // The layout actually rendered in the grid.
  // During resize: applies the live resize dimensions + repack WITHOUT touching currentLayout state.
  // During drag:   currentLayout.gridLayout already holds the swap preview (set in handleDragMove).
  // Otherwise:     just currentLayout.gridLayout.
  const displayGridLayout = useMemo((): LayoutItem[] => {
    if (resizingId && resizeDims && resizeStateRef.current) {
      const withResize = resizeStateRef.current.base.map((l) =>
        l.i === resizingId ? { ...l, w: resizeDims.w, h: resizeDims.h } : l,
      );
      // autoGrow:false — just push displaced cards aside, don't expand them
      return stabilizeLockedTables(
        repackLayout(
          sanitizeLayoutItems(withResize, widgetIdSet, sizeLimitsById),
          sizeLimitsById,
          { autoGrow: false },
        ),
        currentLayout.widgets,
        defaultLayout.gridLayout,
      );
    }
    return currentLayout.gridLayout;
  }, [resizingId, resizeDims, currentLayout.gridLayout, currentLayout.widgets, defaultLayout.gridLayout, widgetIdSet, sizeLimitsById]);

  // ─── dnd-kit ────────────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    dragStartLayoutRef.current = committedLayoutRef.current.map((l) => ({ ...l }));
    swapTargetRef.current = null;
    dragPlacementRef.current = null;
    setActiveId(String(event.active.id));
  }, []);

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    if (!containerRef.current) return;
    const translatedRect = event.active.rect.current.translated;
    if (!translatedRect) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const centerX = translatedRect.left + translatedRect.width  / 2;
    const centerY = translatedRect.top  + translatedRect.height / 2;
    const { gx, gy } = pixelToGrid(centerX, centerY, containerRect);

    const sourceId  = String(event.active.id);
    const sourceItem = dragStartLayoutRef.current.find((item) => item.i === sourceId);
    if (!sourceItem) return;
    const target    = dragStartLayoutRef.current.find(
      (item) => item.i !== sourceId && containsGridPoint(item, gx, gy),
    );
    const newTarget = target?.i ?? null;
    const placement = {
      x: Math.max(0, Math.min(gx, COLS - sourceItem.w)),
      y: Math.max(0, gy),
    };

    if (newTarget) {
      if (
        newTarget === swapTargetRef.current &&
        dragPlacementRef.current === null
      ) {
        return;
      }
      swapTargetRef.current = newTarget;
      dragPlacementRef.current = null;
      const swapped = stabilizeLockedTables(
        applyHardSwapToLayout(dragStartLayoutRef.current, sourceId, newTarget),
        currentLayout.widgets,
        defaultLayout.gridLayout,
      );
      startTransition(() => {
        setCurrentLayout((prev) => ({ ...prev, gridLayout: swapped }));
      });
    } else {
      if (
        swapTargetRef.current === null &&
        dragPlacementRef.current?.x === placement.x &&
        dragPlacementRef.current?.y === placement.y
      ) {
        return;
      }
      swapTargetRef.current = null;
      dragPlacementRef.current = placement;
      const moved = stabilizeLockedTables(
        repackLayout(
          sanitizeLayoutItems(
            applyPreferredPlacement(dragStartLayoutRef.current, sourceId, placement),
            widgetIdSet,
            sizeLimitsById,
          ),
          sizeLimitsById,
          {
            autoGrow: false,
            pinnedItemId: sourceId,
            preferredPlacements: new Map([[sourceId, placement]]),
          },
        ),
        currentLayout.widgets,
        defaultLayout.gridLayout,
      );
      startTransition(() => {
        setCurrentLayout((prev) => ({ ...prev, gridLayout: moved }));
      });
    }
  }, [currentLayout.widgets, defaultLayout.gridLayout, sizeLimitsById, widgetIdSet]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const sourceId = String(event.active.id);
    const dragPlacement = dragPlacementRef.current;
    const final = swapTargetRef.current
      ? repackLayout(
          sanitizeLayoutItems(
            applyHardSwapToLayout(dragStartLayoutRef.current, sourceId, swapTargetRef.current),
            widgetIdSet,
            sizeLimitsById,
          ),
          sizeLimitsById,
          { autoGrow: false },
        )
      : dragPlacement
        ? repackLayout(
            sanitizeLayoutItems(
              applyPreferredPlacement(dragStartLayoutRef.current, sourceId, dragPlacement),
              widgetIdSet,
              sizeLimitsById,
            ),
            sizeLimitsById,
            {
              autoGrow: false,
              pinnedItemId: sourceId,
              preferredPlacements: new Map([[sourceId, dragPlacement]]),
            },
          )
        : repackLayout(
            sanitizeLayoutItems(dragStartLayoutRef.current, widgetIdSet, sizeLimitsById),
            sizeLimitsById,
            { autoGrow: false },
          );

    const repacked = stabilizeLockedTables(final, currentLayout.widgets, defaultLayout.gridLayout);
    committedLayoutRef.current = repacked.map((l) => ({ ...l }));
    setCurrentLayout((prev) => ({ ...prev, gridLayout: repacked }));
    setActiveId(null);
    swapTargetRef.current = null;
    dragPlacementRef.current = null;
  }, [currentLayout.widgets, defaultLayout.gridLayout, sizeLimitsById, widgetIdSet]);

  const handleDragCancel = useCallback(() => {
    setCurrentLayout((prev) => ({ ...prev, gridLayout: [...committedLayoutRef.current] }));
    setActiveId(null);
    swapTargetRef.current = null;
    dragPlacementRef.current = null;
  }, []);

  // ─── Resize ──────────────────────────────────────────────────────────────────
  const handleResizeStart = useCallback((id: string, e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const item = committedLayoutRef.current.find((l) => l.i === id);
    if (!item) return;

    resizeStateRef.current = {
      id,
      startX: e.clientX,
      startY: e.clientY,
      startW: item.w,
      startH: item.h,
      base: committedLayoutRef.current.map((l) => ({ ...l })), // stable snapshot
      item,
    };
    finalResizeDimsRef.current = null;

    setResizingId(id);
    setResizeDims({ w: item.w, h: item.h });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  // pointermove / pointerup — deliberately omit currentLayout.gridLayout from deps
  // to prevent the "setState → re-render → effect re-run → setState" infinite loop.
  useEffect(() => {
    if (!isEditMode) return;

    const onMove = (e: PointerEvent) => {
      const rs = resizeStateRef.current;
      if (!rs) return;

      const cellW = getCellWidth(containerWidth);
      const cellH = ROW_HEIGHT + GAP;
      const limits = sizeLimitsById.get(rs.id);
      const minW = Math.max(1, limits?.minW ?? 1);
      const maxW = Math.max(minW, Math.min(COLS - rs.item.x, limits?.maxW ?? COLS));
      const minH = Math.max(1, limits?.minH ?? 1);
      const maxH = Math.max(minH, limits?.maxH ?? Number.MAX_SAFE_INTEGER);
      const newW = Math.max(minW, Math.min(maxW, rs.startW + Math.round((e.clientX - rs.startX) / (cellW + GAP))));
      const newH = Math.max(minH, Math.min(maxH, rs.startH + Math.round((e.clientY - rs.startY) / cellH)));

      // Track latest dims in a ref so onUp always reads the final value
      finalResizeDimsRef.current = { w: newW, h: newH };

      // Only update the lightweight resize-dims state — NOT currentLayout
      startTransition(() => {
        setResizeDims({ w: newW, h: newH });
      });
    };

    const onUp = () => {
      const rs = resizeStateRef.current;
      if (!rs) return;

      const dims = finalResizeDimsRef.current ?? { w: rs.startW, h: rs.startH };
      const withResize = rs.base.map((l) =>
        l.i === rs.id ? { ...l, w: dims.w, h: dims.h } : l,
      );
      const repacked = stabilizeLockedTables(
        repackLayout(
          sanitizeLayoutItems(withResize, widgetIdSet, sizeLimitsById),
          sizeLimitsById,
          { autoGrow: false },
        ),
        currentLayout.widgets,
        defaultLayout.gridLayout,
      );

      // Commit once — the only setCurrentLayout call during the entire resize
      committedLayoutRef.current = repacked.map((l) => ({ ...l }));
      setCurrentLayout((prev) => ({ ...prev, gridLayout: repacked }));

      resizeStateRef.current = null;
      finalResizeDimsRef.current = null;
      setResizingId(null);
      setResizeDims(null);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, containerWidth, currentLayout.widgets, defaultLayout.gridLayout, sizeLimitsById, widgetIdSet]);

  // ─── Edit controls ───────────────────────────────────────────────────────────
  const handleEditMode = useCallback(() => {
    if (!canEdit) return;
    setSavedLayout(currentLayout);
    setIsEditMode(true);
  }, [canEdit, currentLayout]);

  const handleSave = useCallback(() => {
    setSavedLayout(currentLayout);
    setIsEditMode(false);
  }, [currentLayout]);

    const handleCancel = useCallback(() => {
    committedLayoutRef.current = savedLayout.gridLayout.map((l) => ({ ...l }));
    setCurrentLayout(savedLayout);
    setIsEditMode(false);
  }, [savedLayout]);

  // ─── Add / Remove ────────────────────────────────────────────────────────────
  const handleAddWidget = useCallback((widget: WidgetConfig, size: { w: number; h: number }) => {
    if (!canEdit) return;
    pendingScrollWidgetIdRef.current = widget.id;
    setCurrentLayout((prev) => {
      const newWidgets = [...prev.widgets, widget];
      const nextSizeLimits = buildSizeLimitsById(newWidgets);
      const newItem: LayoutItem = {
        i: widget.id,
        x: 0,
        y: getInsertStartY(prev.gridLayout, prev.widgets),
        w: size.w,
        h: size.h,
      };
      const newGrid = repackLayout(
        sanitizeLayoutItems([...prev.gridLayout, newItem], new Set(newWidgets.map((w) => w.id)), nextSizeLimits),
        nextSizeLimits,
        { autoGrow: false },
      );
      const stabilizedGrid = stabilizeLockedTables(newGrid, newWidgets, defaultLayout.gridLayout);
      committedLayoutRef.current = stabilizedGrid.map((l) => ({ ...l }));
      return { widgets: newWidgets, gridLayout: stabilizedGrid };
    });
    setShowWidgetPicker(false);
  }, [canEdit, defaultLayout.gridLayout]);

  const handleWidgetTypeSelect = useCallback((type: string) => {
    setShowWidgetPicker(false);
    if (type === "customChart") {
      setCustomChartState({});
      return;
    }
    setConfigDialogState({ widgetType: type, widget: null, layoutItem: null });
  }, []);

  const handleRemoveWidget = useCallback((id: string) => {
    setCurrentLayout((prev) => {
      const nextWidgets = prev.widgets.filter((w) => w.id !== id);
      const nextSizeLimits = buildSizeLimitsById(nextWidgets);
      const newGrid = repackLayout(
        sanitizeLayoutItems(
          prev.gridLayout.filter((l) => l.i !== id),
          new Set(nextWidgets.map((w) => w.id)),
          nextSizeLimits,
        ),
        nextSizeLimits,
        { autoGrow: false },
      );
      const stabilizedGrid = stabilizeLockedTables(newGrid, nextWidgets, defaultLayout.gridLayout);
      committedLayoutRef.current = stabilizedGrid.map((l) => ({ ...l }));
      return { widgets: nextWidgets, gridLayout: stabilizedGrid };
    });
  }, [defaultLayout.gridLayout]);

  const handleConfigureWidget = useCallback((id: string) => {
    const targetWidget = currentLayout.widgets.find((widget) => widget.id === id);
    const targetLayoutItem = currentLayout.gridLayout.find((item) => item.i === id) ?? null;
    if (!targetWidget) return;

    if (targetWidget.type === "customChart") {
      setCustomChartState({ widget: targetWidget, layoutItem: targetLayoutItem });
      return;
    }

    setConfigDialogState({
      widgetType: targetWidget.type,
      widget: targetWidget,
      layoutItem: targetLayoutItem,
    });
  }, [currentLayout.gridLayout, currentLayout.widgets]);

  const handleConfiguredWidgetSave = useCallback((nextWidget: WidgetConfig, size?: { w: number; h: number }) => {
    if (configDialogState?.widget || customChartState?.widget) {
      setCurrentLayout((prev) => {
        const newWidgets = prev.widgets.map((widget) =>
          widget.id === nextWidget.id ? nextWidget : widget,
        );
        const nextSizeLimits = buildSizeLimitsById(newWidgets);
        const stabilizedGrid = stabilizeLockedTables(
          repackLayout(
            sanitizeLayoutItems(prev.gridLayout, new Set(newWidgets.map((widget) => widget.id)), nextSizeLimits),
            nextSizeLimits,
            { autoGrow: false },
          ),
          newWidgets,
          defaultLayout.gridLayout,
        );
        committedLayoutRef.current = stabilizedGrid.map((item) => ({ ...item }));
        return {
          widgets: newWidgets,
          gridLayout: stabilizedGrid,
        };
      });
    } else {
      handleAddWidget(nextWidget, size ?? WIDGET_REGISTRY[nextWidget.type]?.defaultSize ?? { w: 4, h: 4 });
    }

    setConfigDialogState(null);
    setCustomChartState(null);
  }, [configDialogState?.widget, customChartState?.widget, defaultLayout.gridLayout, handleAddWidget]);

  const handlePresetLoad = useCallback((layout: DashboardLayout, presetId: string | null) => {
    const normalizedLayout = normalizeDashboardLayout(layout, defaultLayout);
    committedLayoutRef.current = normalizedLayout.gridLayout.map((l) => ({ ...l }));
    setCurrentLayout(normalizedLayout);
    setSavedLayout(normalizedLayout);
    setActivePresetId(presetId);
  }, [defaultLayout]);

  // ─── Widget renderer ─────────────────────────────────────────────────────────
  const renderWidget = useCallback((widget: WidgetConfig) => {
    const entry = WIDGET_REGISTRY[widget.type];
    if (!entry) {
      return (
        <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
          Unknown widget type: {widget.type}
        </div>
      );
    }
    const W = entry.component;
    return <W config={widget.config} title={widget.title} isEditMode={isEditMode} />;
  }, [isEditMode]);

  // Overlay uses the drag-start pos (stable size, before any swap)
  const activeWidget = activeId ? currentLayout.widgets.find((w) => w.id === activeId) : null;
  const activePos    = activeId ? (dragStartLayoutRef.current.find((l) => l.i === activeId) ?? displayGridLayout.find((l) => l.i === activeId)) : null;

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${COLS}, 1fr)`,
    gridAutoRows: `${ROW_HEIGHT}px`,
    gap: `${GAP}px`,
  };
  const displayLayoutSignature = useMemo(
    () => getLayoutSignature(displayGridLayout),
    [displayGridLayout],
  );
  const isInteractiveEditMode = canEdit && isEditMode;
  const gridContent = (
    <div ref={containerRef} style={gridStyle}>
      <LayoutGroup id={`${page}-editor-grid`}>
        {currentLayout.widgets.map((widget) => {
          const pos = displayGridLayout.find((l) => l.i === widget.id);
          if (!pos) return null;
          const isLocked = widget.type === "table";
          const showEditorPreview = isInteractiveEditMode && !isLocked;
          return (
            <DraggableCell
              key={widget.id}
              widget={widget}
              pos={pos}
              isEditMode={isInteractiveEditMode}
              isLocked={isLocked}
              isActive={activeId === widget.id}
              containerWidth={containerWidth}
              layoutKey={displayLayoutSignature}
              onRemove={handleRemoveWidget}
              onConfigure={handleConfigureWidget}
              onResizeStart={handleResizeStart}
            >
              {showEditorPreview ? (
                <EditorPreviewCard
                  widget={widget}
                  pos={pos}
                  isActive={activeId === widget.id}
                  isResizing={resizingId === widget.id}
                />
              ) : (
                renderWidget(widget)
              )}
            </DraggableCell>
          );
        })}
      </LayoutGroup>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">{header}</div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <PresetSelector
              page={page}
              defaultLayout={defaultLayout}
              activePresetId={activePresetId}
              onLoad={handlePresetLoad}
              currentLayout={currentLayout}
            />
            {!isEditMode ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleEditMode}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
                title="Customize layout"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="hidden sm:inline">Edit</span>
              </motion.button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowWidgetPicker(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-[var(--brand-primary)] text-white hover:opacity-90 transition-opacity"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Widget
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {filterBar}
      {childrenPlacement === "beforeGrid" ? children : null}

      {/* Grid */}
      {canEdit ? (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {gridContent}

          {/* Floating overlay — only Framer Motion usage during edit mode */}
          <DragOverlay dropAnimation={null}>
            {activeWidget && activePos ? (
              <OverlayCard widget={activeWidget} pos={activePos} containerWidth={containerWidth}>
                {renderWidget(activeWidget)}
              </OverlayCard>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        gridContent
      )}

      {childrenPlacement === "afterGrid" ? children : null}

      <AnimatePresence>
        {showWidgetPicker && (
          <WidgetPicker
            page={page}
            onSelectType={handleWidgetTypeSelect}
            onClose={() => setShowWidgetPicker(false)}
          />
        )}
        {configDialogState && (
          <WidgetConfigDialog
            widgetType={configDialogState.widgetType}
            widget={configDialogState.widget ?? null}
            layoutItem={configDialogState.layoutItem ?? null}
            onSave={handleConfiguredWidgetSave}
            onClose={() => setConfigDialogState(null)}
          />
        )}
        {customChartState && (
          <CustomChartBuilder
            initialWidget={customChartState.widget ?? null}
            onAdd={handleConfiguredWidgetSave}
            onClose={() => setCustomChartState(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
